<template>
  <div class="rise-in space-y-6">
    <!-- Header card -->
    <div class="card card-pad">
      <div
        class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
      >
        <div>
          <h1 class="page-title">Compliance Audit Log</h1>
        </div>
        <button
          class="btn btn-secondary btn-md self-start sm:self-auto flex items-center gap-1.5"
          :disabled="loading || logs.length === 0"
          @click="exportCSV"
        >
          <font-awesome-icon icon="download" />
          <span>Export CSV</span>
        </button>
      </div>
    </div>

    <!-- Filters & Settings -->
    <div class="card card-pad space-y-4">
      <h2 class="text-xs font-bold uppercase tracking-wider text-ink-muted">
        Filters & Settings
      </h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <!-- Search -->
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by Patient ID or Actor..."
            class="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 focus:border-brand-600 focus:ring focus:ring-brand-100 focus:outline-none text-sm text-ink transition-colors"
          />
          <font-awesome-icon
            icon="search"
            class="absolute left-3.5 top-3.5 text-gray-400 text-sm"
          />
        </div>

        <!-- Table filter -->
        <div>
          <select
            v-model="filterTable"
            class="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-brand-600 focus:outline-none text-sm text-ink bg-white transition-colors"
          >
            <option value="all">All Tables</option>
            <option value="forms">Forms (Clinical Data)</option>
            <option value="patients">Patients</option>
          </select>
        </div>

        <!-- Operation filter -->
        <div>
          <select
            v-model="filterOp"
            class="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-brand-600 focus:outline-none text-sm text-ink bg-white transition-colors"
          >
            <option value="all">All Operations</option>
            <option value="INSERT">INSERT (New Record)</option>
            <option value="UPDATE">UPDATE (Correction)</option>
            <option value="DELETE">DELETE (Removal)</option>
          </select>
        </div>

        <!-- Limit Settings -->
        <div>
          <select
            v-model="limit"
            class="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-brand-600 focus:outline-none text-sm text-ink bg-white transition-colors"
            @change="fetchLogs"
          >
            <option :value="50">Show last 50 logs</option>
            <option :value="100">Show last 100 logs</option>
            <option :value="200">Show last 200 logs</option>
            <option :value="500">Show last 500 logs</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="loading"
      class="flex items-center gap-2 text-ink-muted py-12 justify-center card card-pad"
    >
      <font-awesome-icon icon="circle-notch" spin class="text-brand-600" />
      <span>Fetching audit log…</span>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="text-red-600 text-sm text-center py-6 card card-pad"
    >
      {{ error }}
    </div>

    <!-- Logs List -->
    <div v-else class="space-y-4">
      <div
        v-for="log in filteredLogs"
        :key="log.id"
        class="card overflow-hidden transition-all duration-200"
        :class="
          expandedLogId === log.id
            ? 'border-brand-200 ring-1 ring-brand-100'
            : 'hover:border-gray-200'
        "
      >
        <!-- Log Row Header (Clickable to Expand) -->
        <div
          class="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer select-none"
          @click="toggleExpand(log.id)"
        >
          <div class="flex items-start gap-3 flex-1 min-w-0">
            <span
              class="px-2.5 py-1 rounded-lg text-xs font-bold text-center w-20 flex-shrink-0 uppercase"
              :class="opBadgeClass(log.operation)"
            >
              {{ log.operation }}
            </span>
            <div class="min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-bold text-ink text-sm">{{
                  log.table_name === 'forms'
                    ? 'Clinical Form'
                    : 'Patient Record'
                }}</span>
                <span class="text-xs text-ink-muted">Row PK:</span>
                <code
                  class="text-xs font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100 text-ink-soft select-all"
                  @click.stop
                >
                  {{ log.row_pk }}
                </code>
              </div>
              <p class="text-xs text-ink-soft mt-1 truncate">
                Changed by
                <span class="font-semibold text-ink">{{
                  log.actor_email || 'system'
                }}</span>
                ({{ log.actor_role || 'system' }})
              </p>
            </div>
          </div>

          <div class="flex items-center justify-between md:justify-end gap-4">
            <div class="text-left md:text-right">
              <span class="text-xs text-ink-muted block">
                {{ moment(log.changed_at).format('DD.MM.YYYY HH:mm:ss') }}
              </span>
            </div>
            <font-awesome-icon
              :icon="expandedLogId === log.id ? 'chevron-up' : 'chevron-down'"
              class="text-ink-muted text-sm transition-transform duration-200"
            />
          </div>
        </div>

        <!-- Log Row Details (Expanded) -->
        <div
          v-if="expandedLogId === log.id"
          class="border-t border-gray-100 bg-gray-50/50 p-6 space-y-4"
        >
          <!-- Value diffing -->
          <div class="space-y-2">
            <h4
              class="text-xs font-bold text-ink-muted uppercase tracking-wider"
            >
              Changed Values Detail
            </h4>

            <div
              v-if="log.operation === 'UPDATE'"
              class="border border-gray-100 rounded-xl overflow-hidden bg-white"
            >
              <div
                class="grid grid-cols-3 gap-2 px-4 py-2 bg-gray-50 text-xs font-bold text-ink-muted uppercase tracking-wider"
              >
                <span>Field</span>
                <span>Original Value</span>
                <span>Corrected Value</span>
              </div>
              <div class="divide-y divide-gray-100">
                <div
                  v-for="diff in diffFields(log)"
                  :key="diff.field"
                  class="grid grid-cols-3 gap-2 px-4 py-3 text-sm items-center"
                >
                  <span class="font-semibold text-ink font-mono text-xs">{{
                    diff.field
                  }}</span>
                  <span
                    class="text-red-500 line-through truncate"
                    :title="formatVal(diff.oldValue)"
                  >
                    {{ formatVal(diff.oldValue) }}
                  </span>
                  <span
                    class="text-green-600 font-medium truncate"
                    :title="formatVal(diff.newValue)"
                  >
                    {{ formatVal(diff.newValue) }}
                  </span>
                </div>
                <div
                  v-if="diffFields(log).length === 0"
                  class="p-4 text-center text-sm text-ink-muted"
                >
                  No editable clinical field updates found (only metadata
                  changed).
                </div>
              </div>
            </div>

            <!-- Full JSON representation for INSERT/DELETE or fallback -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div v-if="log.old_data" class="space-y-1.5">
                <span class="text-xs font-semibold text-ink-soft"
                  >Original Record Dump</span
                >
                <pre
                  class="bg-gray-900 text-gray-100 text-xs font-mono p-4 rounded-xl overflow-x-auto max-h-[300px] shadow-inner"
                  >{{ JSON.stringify(log.old_data, null, 2) }}</pre
                >
              </div>
              <div v-if="log.new_data" class="space-y-1.5">
                <span class="text-xs font-semibold text-ink-soft"
                  >New Record Dump</span
                >
                <pre
                  class="bg-gray-900 text-gray-100 text-xs font-mono p-4 rounded-xl overflow-x-auto max-h-[300px] shadow-inner"
                  >{{ JSON.stringify(log.new_data, null, 2) }}</pre
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="filteredLogs.length === 0"
        class="card card-pad text-center py-8 text-ink-muted"
      >
        No audit log records match the filters.
      </div>
    </div>
  </div>
</template>

<script>
import { rest } from 'helpers/api'

export default {
  name: 'AuditLog',
  data() {
    return {
      logs: [],
      searchQuery: '',
      filterTable: 'all',
      filterOp: 'all',
      limit: 200,
      loading: true,
      error: null,
      expandedLogId: null,
    }
  },
  computed: {
    filteredLogs() {
      return this.logs.filter(l => {
        // Table filter
        if (this.filterTable !== 'all' && l.table_name !== this.filterTable) {
          return false
        }
        // Operation filter
        if (this.filterOp !== 'all' && l.operation !== this.filterOp) {
          return false
        }
        // Search filter (Patient ID/PK or actor email)
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase()
          const pkMatch = (l.row_pk || '').toLowerCase().includes(q)
          const actorMatch = (l.actor_email || '').toLowerCase().includes(q)
          return pkMatch || actorMatch
        }
        return true
      })
    },
  },
  created() {
    this.fetchLogs()
  },
  methods: {
    async fetchLogs() {
      this.loading = true
      this.error = null
      try {
        this.logs = await rest.listAudit(this.limit)
      } catch (e) {
        this.error = 'Failed to load audit logs.'
      } finally {
        this.loading = false
      }
    },
    toggleExpand(id) {
      this.expandedLogId = this.expandedLogId === id ? null : id
    },
    opBadgeClass(op) {
      if (op === 'INSERT')
        return 'bg-green-50 text-green-700 border border-green-200'
      if (op === 'UPDATE')
        return 'bg-amber-50 text-amber-700 border border-amber-200'
      return 'bg-red-50 text-red-700 border border-red-200'
    },
    formatVal(val) {
      if (val === null || val === undefined) return '(empty)'
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      if (typeof val === 'object') return JSON.stringify(val)
      return String(val)
    },
    diffFields(log) {
      const diffs = []
      const oldObj = log.old_data
      const newObj = log.new_data
      if (!oldObj || !newObj) return diffs

      // Clinical data in forms is stored inside .data
      const oldData = oldObj.data || oldObj
      const newData = newObj.data || newObj

      const allKeys = new Set([
        ...Object.keys(oldData),
        ...Object.keys(newData),
      ])
      const ignoredKeys = new Set([
        'patientId',
        'projectSlug',
        'updatedAt',
        'copiedAnthropometrics',
        'copiedSmoking',
        'copiedMedication',
        'copiedMorbidities',
        'copiedComorbidities',
      ])

      allKeys.forEach(k => {
        if (ignoredKeys.has(k)) return
        const oldVal = oldData[k]
        const newVal = newData[k]
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          diffs.push({
            field: k,
            oldValue: oldVal,
            newValue: newVal,
          })
        }
      })
      return diffs
    },
    exportCSV() {
      const headers = [
        'ID',
        'Table',
        'Row PK',
        'Operation',
        'Changed At',
        'Actor Email',
        'Actor Role',
      ]
      const rows = this.filteredLogs.map(l => [
        l.id,
        l.table_name,
        l.row_pk,
        l.operation,
        l.changed_at,
        l.actor_email || 'system',
        l.actor_role || 'system',
      ])

      const csvContent =
        'data:text/csv;charset=utf-8,\uFEFF' +
        [
          headers.join(','),
          ...rows.map(r =>
            r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')
          ),
        ].join('\n')

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute(
        'download',
        `dbi_audit_log_${new Date().toISOString().slice(0, 10)}.csv`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
